import { Button, Card } from '@sisumail/ui';
import { AlertTriangle, Check, Copy, RefreshCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '../lib/logger';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    requestId: string | null;
    copied: boolean;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI with diagnostics.
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            requestId: null,
            copied: false,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const requestId = logger.getTraceId();
        this.setState({ requestId });

        // Send to centralized logging service
        logger.captureError('Unhandled React error', error, {
            componentStack: errorInfo.componentStack || undefined,
        });
    }

    handleRetry = () => {
        logger.resetTrace(); // Get new trace for retry
        this.setState({
            hasError: false,
            error: null,
            requestId: null,
            copied: false,
        });
    };

    handleCopyDiagnostics = async () => {
        const diagnostics = {
            requestId: this.state.requestId,
            error: this.state.error?.message,
            stack: this.state.error?.stack,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
        };

        try {
            await navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        } catch (_e) {}
    };

    override render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex min-h-screen items-center justify-center bg-neutral-950 p-6 selection:bg-emerald-500 selection:text-black">
                    <div className="w-full max-w-md">
                        <Card className="border-red-500/20 bg-red-500/[0.02] p-10 text-center">
                            <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-[2rem] border border-red-500/20 bg-red-500/10 ring-8 ring-red-500/[0.02]">
                                <AlertTriangle className="h-10 w-10 text-red-500" />
                            </div>

                            <h1 className="mb-4 font-black text-2xl text-white uppercase italic tracking-tight">
                                Critical Failure
                            </h1>

                            <p className="mb-8 font-medium text-neutral-500 text-sm leading-relaxed">
                                A significant error occurred in the application layer. Our telemetry
                                has been notified.
                            </p>

                            <div className="mb-10 space-y-4">
                                {this.state.requestId && (
                                    <div className="flex flex-col gap-2">
                                        <span className="font-bold text-[10px] text-neutral-600 uppercase leading-none tracking-widest">
                                            Trace Signature
                                        </span>
                                        <code className="rounded-xl border border-neutral-900 bg-neutral-950 p-3 font-bold font-mono text-[10px] text-neutral-400">
                                            {this.state.requestId}
                                        </code>
                                    </div>
                                )}

                                {this.state.error && (
                                    <div className="flex flex-col gap-2">
                                        <span className="font-bold text-[10px] text-neutral-600 uppercase leading-none tracking-widest">
                                            Internal Error
                                        </span>
                                        <code className="break-all rounded-xl border border-red-500/20 bg-red-500/10 p-3 font-bold font-mono text-[10px] text-red-500/80">
                                            {this.state.error.message}
                                        </code>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-4">
                                <Button
                                    onClick={this.handleRetry}
                                    className="h-14 w-full bg-white font-black text-[10px] text-black uppercase tracking-widest"
                                >
                                    <RefreshCw className="mr-2" size={16} />
                                    Restore Session
                                </Button>

                                <button
                                    onClick={this.handleCopyDiagnostics}
                                    className="flex w-full items-center justify-center gap-2 py-2 font-bold text-[10px] text-neutral-600 uppercase tracking-widest transition-colors hover:text-white"
                                >
                                    {this.state.copied ? (
                                        <>
                                            <Check size={14} className="text-emerald-500" />
                                            Report Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy size={14} />
                                            Copy Diagnostic Report
                                        </>
                                    )}
                                </button>
                            </div>
                        </Card>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
